export interface LoadBaiduMapGLOptions {
  ak: string;
  version?: string;
  timeoutMs?: number;
}

const BAIDU_MAP_GL_SCRIPT_ID = 'baidu-map-gl-sdk';
const BAIDU_MAP_GL_VERSION = '1.0';
const BAIDU_MAP_GL_TIMEOUT = 15000;

let loadPromise: Promise<any> | null = null;
let loadedAk: string | null = null;

const toError = (error: unknown, fallbackMessage: string) => {
  if (error instanceof Error) {
    return error;
  }
  return new Error(fallbackMessage);
};

// micro-app 可能代理 window，优先使用原始 window 检测 SDK
type BMapGlobalWindow = Window & {
  rawWindow?: Window;
  BMapGL?: typeof BMapGL;
  __baiduMapGLInit__?: () => void;
  [key: string]: any;
};

const getRawWindow = (): BMapGlobalWindow => {
  const w = window as BMapGlobalWindow;
  return (w.rawWindow || w) as BMapGlobalWindow;
};

export const isBaiduMapGLReady = () => {
  const sdk = getRawWindow().BMapGL;
  return (
    !!sdk &&
    typeof sdk.Map === 'function' &&
    typeof sdk.Point === 'function' &&
    typeof sdk.XYZLayer === 'function'
  );
};

const waitForBaiduMapGL = (timeoutMs: number): Promise<any> => {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const rawWindow = getRawWindow();
    const timer = window.setInterval(() => {
      if (isBaiduMapGLReady()) {
        window.clearInterval(timer);
        resolve(rawWindow.BMapGL);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(timer);
        reject(new Error('Timed out waiting for BMapGL constructors.'));
      }
    }, 50);
  });
};

export const loadBaiduMapGL = ({
  ak,
  version = BAIDU_MAP_GL_VERSION,
  timeoutMs = BAIDU_MAP_GL_TIMEOUT,
}: LoadBaiduMapGLOptions): Promise<any> => {
  const resolvedAk = ak.trim();
  if (!resolvedAk) {
    return Promise.reject(new Error('Baidu Map GL AK is required.'));
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(
      new Error('Baidu Map GL can only be loaded in a browser environment.')
    );
  }

  const rawWindow = getRawWindow();
  const inMicroApp = !!(window as any).__MICRO_APP_ENVIRONMENT__;

  console.log('[BMapLoader] loadBaiduMapGL called', {
    inMicroApp,
    isReady: isBaiduMapGLReady(),
    hasLoadPromise: !!loadPromise,
    loadedAk,
    ak: resolvedAk,
  });

  if (isBaiduMapGLReady()) {
    loadedAk = loadedAk || resolvedAk;
    console.log(
      '[BMapLoader] SDK already ready, reuse from host window',
      { inMicroApp, source: inMicroApp ? 'main-app-preload' : 'existing' }
    );
    return Promise.resolve(rawWindow.BMapGL);
  }
  if (loadPromise) {
    if (loadedAk && loadedAk !== resolvedAk) {
      console.warn(
        '[MapEditor] Baidu Map GL is already loading with a different AK. Reusing the existing loader promise.'
      );
    }
    console.log('[BMapLoader] Reusing existing load promise');
    return loadPromise;
  }

  loadedAk = resolvedAk;
  loadPromise = new Promise((resolve, reject) => {
    const callbackName = '__baiduMapGLInit__';
    const existingScript = document.getElementById(
      BAIDU_MAP_GL_SCRIPT_ID
    ) as HTMLScriptElement | null;
    let createdScript: HTMLScriptElement | null = null;
    let settled = false;
    let timeoutId: number | null = null;

    const cleanup = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (rawWindow[callbackName]) {
        delete rawWindow[callbackName];
      }
    };

    const settleSuccess = (sdk: any) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      console.log('[BMapLoader] SDK loaded successfully');
      resolve(sdk);
    };

    const settleError = (error: unknown) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      if (createdScript?.parentNode) {
        createdScript.parentNode.removeChild(createdScript);
      }
      loadPromise = null;
      loadedAk = null;
      console.error('[BMapLoader] Failed to load Baidu Map GL SDK:', error);
      reject(toError(error, 'Failed to load Baidu Map GL.'));
    };

    timeoutId = window.setTimeout(() => {
      console.error('[BMapLoader] Timeout waiting for BMapGL SDK');
      settleError(new Error('Timed out loading Baidu Map GL SDK.'));
    }, timeoutMs);

    // 在原始 window 上注册回调，避免 micro-app 代理 window 导致回调找不到
    rawWindow[callbackName] = () => {
      console.log('[BMapLoader] Baidu callback invoked');
      waitForBaiduMapGL(timeoutMs).then(settleSuccess).catch(settleError);
    };

    if (existingScript) {
      console.log('[BMapLoader] Existing script found, wait for SDK ready');
      waitForBaiduMapGL(timeoutMs).then(settleSuccess).catch(settleError);
      return;
    }

    createdScript = document.createElement('script');
    createdScript.id = BAIDU_MAP_GL_SCRIPT_ID;
    createdScript.async = true;
    createdScript.defer = true;

    createdScript.src = `https://api.map.baidu.com/api?v=${encodeURIComponent(
      version
    )}&type=webgl&ak=${encodeURIComponent(resolvedAk)}&callback=${callbackName}`;

    createdScript.onerror = (event) => {
      console.error('[BMapLoader] Script onerror', event);
      settleError(new Error('Failed to load Baidu Map GL SDK script.'));
    };
    createdScript.onload = () => {
      console.log('[BMapLoader] Script onload fired');
      createdScript?.setAttribute('data-loaded', 'true');
      waitForBaiduMapGL(timeoutMs).then(settleSuccess).catch(settleError);
    };

    console.log('[BMapLoader] Appending script to document.head:', createdScript.src);
    document.head.appendChild(createdScript);
  });

  return loadPromise;
};
